import {
    Alert,
    Box as MuiBox,
    ButtonBase,
    CircularProgress,
    Dialog,
    Divider,
    FormControl,
    FormLabel,
    MenuItem,
    Select,
    Tab,
    Tabs,
    Tooltip,
    Typography,
} from "@mui/material";
import type { GPUTier } from "@novorender/api";
import { getDeviceProfile } from "@novorender/api";
import { ChevronRight, Download, Error as ErrorIcon, Home, Trash } from "@procore/core-icons";
import {
    Box,
    Button,
    colors,
    ConfirmModal,
    Modal,
    Spinner,
    Typography as ProcoreTypography,
} from "@procore/core-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { WizardTrigger } from "apis/procore";
import { getModelHomeView, saveModelHomeViewpoint } from "apis/procore/bimModelHomeView";
import { buildModelRevisionsArgs, selectSceneRevision } from "apis/procore/modelRevisions";
import { useLazyGetModelRevisionsQuery } from "apis/procore/procoreApi";
import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { ToggleWithText } from "components/toggleWithText";
import { FeatureToggle } from "config/featureToggles";
import { StorageKey } from "config/storage";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { highlightActions, useDispatchHighlighted } from "contexts/highlighted";
import { usePanelPickerOverlay } from "contexts/panelPickerOverlay";
import { openGuidanceWizard } from "features/gpuDetection/guidanceStore";
import { useGuidanceSnapshot } from "features/gpuDetection/useGuidanceSnapshot";
import { selectIsMiniMapWidgetShown } from "features/miniMap/minimapSlice";
import { useOfflineSceneDirName } from "features/offline";
import { offlineSceneSummary } from "features/offline/offlineScreen";
import { offlineActions, selectOfflineAction, selectOfflineScenes } from "features/offline/offlineSlice";
import {
    ENVIRONMENT_PRESETS,
    type EnvironmentPresetId,
    getEnvironmentPresetBackground,
    getEnvironmentPresetId,
    renderActions,
    selectBackground,
    selectCameraDefaults,
    selectDeviceProfile,
} from "features/render";
import { HeadlightSettings } from "features/render/HeadlightSettings";
import { abortSharedSelection } from "features/render/hooks/sharedSelectionAbort";
import { discardPendingContextMenuPaint } from "features/render/hooks/useCanvasContextMenuHandler";
import { useSetController } from "features/render/hooks/useSetController";
import { SelectionResolution } from "features/render/types";
import { viewpointCameraToCadCamera } from "features/render/utils";
import {
    clearDroneOnNextConnect,
    requestDroneOnNextConnect,
    spaceMouseNoteKey,
    spaceMouseNoteSeverity,
    useLoopbackPermissionState,
    useSpaceMouseAccess,
    useSpaceMouseStatus,
} from "features/spaceMouse";
import { UnitSystem } from "features/uom/spec";
import { useGetViewpoint } from "features/viewpoints/useGetViewpoint";
import { useHasModelRevision } from "hooks/useHasModelRevision";
import { useSceneId } from "hooks/useSceneId";
import {
    explorerActions,
    selectConfig,
    selectIsAxisSphereVisible,
    selectIsJoysticksVisible,
    selectIsMiniMapVisible,
    selectIsOnline,
    selectIsProcoreMobileDesign,
    selectProcoreCompanyId,
    selectProcoreContext,
    selectProcoreProjectId,
    selectSelectionResolution,
    selectUnitSystem,
} from "slices/explorer";
import { selectIsAdminScene } from "slices/explorer/selectors";
import { persistFeatureToggle, useFeatureToggle } from "slices/featureToggles";
import { useIsSettingsSectionVisible } from "slices/settingsSectionVisibility";
import { telemetry } from "telemetry";
import { formatFileSizeMetric } from "utils/misc";
import { saveToStorage } from "utils/storage";

import { SETTINGS_SECTION_IDS } from "../constants";
import { type PickerOption, SettingsPickerView } from "./SettingsPickerView";
import { ShortcutsTab } from "./ShortcutsTab";

type PickerKind = "unitSystem" | "performance" | "selectionResolution";

interface SettingsContentProps {
    useSlideInPicker?: boolean;
}

type SettingsTab = "general" | "shortcuts";

function getTierKey(tier: GPUTier): string {
    switch (tier) {
        case 0:
            return "low";
        case 1:
            return "medium";
        case 2:
            return "high";
        case 3:
            return "ultra";
        default:
            return "medium";
    }
}

const DEVICE_PROFILES = [0, 1, 2, 3].map((tier) => getDeviceProfile(tier as GPUTier));

const SELECTION_RESOLUTION_OPTIONS = [
    { value: SelectionResolution.FirstObject, key: "firstObject" },
    { value: SelectionResolution.LastObject, key: "lastObject" },
] as const;

/** Procore palette tokens from the Spacemouse GA design, in place of MUI's stock Alert colors. */
const SPACE_MOUSE_NOTE_COLORS = {
    warning: { color: colors.yellow20, backgroundColor: colors.yellow94, borderColor: colors.yellow85 },
    error: { color: colors.red30, backgroundColor: colors.red94, borderColor: colors.red90 },
} as const;

/**
 * Circular download-progress control with a centered stop square. Replaces the
 * Download button while a sync is in progress; clicking it aborts the download
 * (both the scan/"waiting" phase and the actual download are abortable via the
 * shared abort signal -- see useHandleOffline.ts "pause"). Determinate while a
 * percentage is known, indeterminate during the scan phase.
 */
function DownloadProgressControl({
    determinate,
    value,
    onCancel,
}: {
    determinate: boolean;
    value: number;
    onCancel: () => void;
}) {
    const size = 36;
    const thickness = 3.6;
    return (
        <ButtonBase
            onClick={onCancel}
            aria-label="Cancel download"
            sx={{ position: "relative", width: size, height: size, borderRadius: "50%" }}
            data-testid="settings-model-cancel-button"
        >
            {/* Light track behind the progress arc */}
            <CircularProgress
                variant="determinate"
                value={100}
                size={size}
                thickness={thickness}
                sx={{ position: "absolute", top: 0, left: 0, color: "rgba(0, 0, 0, 0.08)" }}
            />
            {/* Progress arc (rounded ends to match Figma) */}
            <CircularProgress
                variant={determinate ? "determinate" : "indeterminate"}
                value={determinate ? value : undefined}
                size={size}
                thickness={thickness}
                sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    color: "text.primary",
                    "& .MuiCircularProgress-circle": { strokeLinecap: "round" },
                }}
            />
            {/* Center stop square */}
            <MuiBox sx={{ width: 12, height: 12, borderRadius: "3px", backgroundColor: "text.primary" }} />
        </ButtonBase>
    );
}

/**
 * Model Download section for the Settings panel.
 * Shows Download (fullSync) or Remove from Device based on sync status,
 * with a circular progress/abort control while scanning/downloading. Status is
 * driven by the SDK's offline state machine (see useHandleOffline.ts).
 */
function ModelDownloadSection({ sceneKey }: { sceneKey: string }) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const scenes = useAppSelector(selectOfflineScenes);
    const action = useAppSelector(selectOfflineAction);
    const isOnline = useAppSelector(selectIsOnline);
    const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);

    const scene = offlineSceneSummary(scenes[sceneKey]);
    const status = scene?.status;
    const isSynced = status === "synchronized";
    // True while an offline action is dispatched or the SDK reports in-progress status.
    const isWorking = action !== undefined || status === "synchronizing" || status === "scanning";
    // The circular progress/abort control represents a user-initiated full
    // download only -- not background incremental sync / estimate / readSize.
    const isFullSyncing = action?.action === "fullSync";
    const progressPercent = scene?.progress ? Number.parseInt(scene.progress, 10) : NaN;
    const hasDeterminateProgress = Number.isFinite(progressPercent);

    // A new run starts its progress over: the circle is indeterminate until its first tick.
    const handleDownload = () => {
        dispatch(offlineActions.setAction({ action: "fullSync" }));
    };

    // Aborts the in-progress sync (scan or download). "pause" triggers the
    // shared abort signal in useHandleOffline, cancelling the current operation.
    const handleCancel = () => {
        dispatch(offlineActions.setAction({ action: "pause" }));
    };

    const handleRemoveClick = () => {
        setConfirmRemoveOpen(true);
    };

    const handleConfirmRemove = () => {
        setConfirmRemoveOpen(false);
        dispatch(offlineActions.setAction({ action: "delete", id: sceneKey }));
    };

    const handleCancelRemove = () => {
        setConfirmRemoveOpen(false);
    };

    return (
        <Box display="flex" flexDirection="column" gap="0.5rem">
            <FormLabel sx={{ fontWeight: 600, color: "text.primary" }}>{t("modelDownload.title")}</FormLabel>
            <Typography variant="body1" color="text.secondary">
                {isSynced
                    ? t("modelDownload.syncedMessage", {
                          size: scene?.size ? formatFileSizeMetric(scene.size) : "",
                      })
                    : t("modelDownload.description")}
            </Typography>
            <Box>
                {isFullSyncing ? (
                    // Only a user-initiated full download shows the circular control;
                    // it replaces the button and doubles as the abort affordance.
                    <DownloadProgressControl
                        determinate={hasDeterminateProgress}
                        value={hasDeterminateProgress ? progressPercent : 0}
                        onCancel={handleCancel}
                    />
                ) : isSynced ? (
                    <Button
                        variant="secondary"
                        icon={<Trash />}
                        // Disabled offline: removing the only offline copy with no
                        // way to re-download would strand the user. Also disabled
                        // while another offline action is in progress.
                        disabled={!isOnline || isWorking}
                        onClick={handleRemoveClick}
                        data-testid="settings-model-remove-button"
                    >
                        {t("modelDownload.remove")}
                    </Button>
                ) : (
                    <Button
                        variant="secondary"
                        icon={<Download />}
                        disabled={!isOnline || isWorking}
                        onClick={handleDownload}
                        data-testid="settings-model-download-button"
                    >
                        {t("modelDownload.download")}
                    </Button>
                )}
            </Box>

            {/* Download failure banner (Figma: background-notification-error / text-error).
                Shown after a user-initiated full sync fails -- the only work that reports
                an error status; background incremental caching reports none. The Download
                button above doubles as retry. */}
            {status === "error" && !isWorking && (
                <Box
                    role="alert"
                    display="flex"
                    alignItems="center"
                    gap="4px"
                    padding="8px"
                    style={{ backgroundColor: "#FDF7F7", borderRadius: "3px" }}
                    data-testid="settings-model-download-error"
                >
                    <ErrorIcon size="sm" style={{ color: "#AD1F1F", flexShrink: 0 }} />
                    <ProcoreTypography intent="small" style={{ color: "#AD1F1F", letterSpacing: "normal" }}>
                        {t("modelDownload.downloadFailed")}
                    </ProcoreTypography>
                </Box>
            )}

            {/* iOS-style native alert per Figma. Custom because the section is mobile-only. */}
            <Dialog
                open={confirmRemoveOpen}
                onClose={handleCancelRemove}
                // Disable MUI's default size container so PaperProps width wins.
                maxWidth={false}
                PaperProps={{
                    sx: {
                        width: 244,
                        minWidth: 244,
                        height: 230,
                        borderRadius: "14px",
                        m: 0,
                        // Figma: --native-ios-materials-regular
                        backgroundColor: "rgba(248, 248, 248, 0.82)",
                        backdropFilter: "blur(20px)",
                        WebkitBackdropFilter: "blur(20px)",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                    },
                }}
                BackdropProps={{
                    // Figma: --background-overlay
                    sx: { backgroundColor: "rgba(0, 0, 0, 0.8)" },
                }}
            >
                {/* Text container per Figma; top-anchored so padding-top is the visible top spacing. */}
                <MuiBox
                    sx={{
                        height: 140,
                        boxSizing: "border-box",
                        pt: "19px",
                        pr: "16px",
                        pb: "17px",
                        pl: "16px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        textAlign: "center",
                        gap: "2px",
                    }}
                >
                    <ProcoreTypography intent="body" weight="semibold">
                        {t("modelDownload.confirmRemoveTitle")}
                    </ProcoreTypography>
                    <ProcoreTypography intent="small">{t("modelDownload.confirmRemoveBody")}</ProcoreTypography>
                </MuiBox>
                <Divider />
                {/* Buttons: 44px each, transparent background, iOS-blue text (Remove semibold). */}
                <ButtonBase
                    onClick={handleConfirmRemove}
                    data-testid="settings-model-remove-confirm-button"
                    sx={{ height: 44, width: "100%" }}
                >
                    <ProcoreTypography intent="body" weight="semibold" style={{ color: "rgba(0, 122, 255, 1)" }}>
                        {t("modelDownload.confirmRemoveAction")}
                    </ProcoreTypography>
                </ButtonBase>
                <Divider />
                <ButtonBase
                    onClick={handleCancelRemove}
                    data-testid="settings-model-remove-cancel-button"
                    sx={{ height: 44, width: "100%" }}
                >
                    <ProcoreTypography intent="body" style={{ color: "rgba(0, 122, 255, 1)" }}>
                        {t("cancel")}
                    </ProcoreTypography>
                </ButtonBase>
            </Dialog>
        </Box>
    );
}

const sectionLabelSx = { fontWeight: 600, color: "text.primary", mb: 1 } as const;

const pickerRowSx = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0.625rem 0.75rem",
    borderRadius: "4px",
    border: "1px solid rgba(0, 0, 0, 0.23)",
    backgroundColor: "white",
    cursor: "pointer",
    width: "100%",
    textAlign: "left" as const,
    color: "text.primary",
    "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
    "&:focus-visible": { outline: "2px solid #1976d2", outlineOffset: "-2px" },
} as const;

interface PickerRowProps {
    label: string;
    valueLabel: string;
    onClick: () => void;
    disabled?: boolean;
    testId?: string;
}

function PickerRow({ label, valueLabel, onClick, disabled, testId }: PickerRowProps) {
    return (
        <FormControl fullWidth size="small">
            <FormLabel sx={sectionLabelSx}>{label}</FormLabel>
            <MuiBox
                component="button"
                type="button"
                onClick={disabled ? undefined : onClick}
                sx={{
                    ...pickerRowSx,
                    ...(disabled ? { opacity: 0.5, cursor: "default" } : {}),
                }}
                data-testid={testId}
            >
                <Typography variant="body2" sx={{ color: "text.primary" }}>
                    {valueLabel}
                </Typography>
                <ChevronRight />
            </MuiBox>
        </FormControl>
    );
}

export function SettingsContent({ useSlideInPicker = false }: SettingsContentProps = {}) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const dispatchHighlighted = useDispatchHighlighted();
    const unitSystem = useAppSelector(selectUnitSystem);
    const deviceProfile = useAppSelector(selectDeviceProfile);
    const selectionResolution = useAppSelector(selectSelectionResolution);
    const background = useAppSelector(selectBackground);
    const assetsUrl = useAppSelector(selectConfig).assetsUrl;
    const companyId = useAppSelector(selectProcoreCompanyId);
    const projectId = useAppSelector(selectProcoreProjectId);
    const procoreContext = useAppSelector(selectProcoreContext);
    const isMiniMapVisible = useAppSelector(selectIsMiniMapVisible);
    const isAxisSphereVisible = useAppSelector(selectIsAxisSphereVisible);
    const isProcoreMobileDesign = useAppSelector(selectIsProcoreMobileDesign);
    const isJoysticksVisible = useAppSelector(selectIsJoysticksVisible);
    const { settingOn: isSpaceMouseEnabled, desktopAvailable: isSpaceMouseDesktop } = useSpaceMouseAccess();
    const pinholeController = useAppSelector(selectCameraDefaults).pinhole.controller;
    const setController = useSetController();
    const spaceMousePermission = useLoopbackPermissionState();
    const spaceMouseStatus = useSpaceMouseStatus();
    const spaceMouseNote = spaceMouseNoteKey(spaceMousePermission, isSpaceMouseEnabled, spaceMouseStatus);
    const spaceMouseNoteLevel = spaceMouseNote ? spaceMouseNoteSeverity(spaceMouseNote) : null;
    const isUpdateHomeViewEnabled = useFeatureToggle(FeatureToggle.UpdateHomeView);
    const showHeadlightSettings = !useSlideInPicker && window.location.search.includes("debug=true");
    // Gate offline download / Update Home View to Model Manager models only.
    const { hasModelRevision } = useHasModelRevision();
    const isAdminScene = useAppSelector(selectIsAdminScene);
    const isOnline = useAppSelector(selectIsOnline);
    // The mini map toggle is enabled exactly when a mini map widget is on screen,
    // so anything the user can see is also something they can hide — including the
    // loading shimmer and the desktop empty-state card, neither of which used to
    // enable it. Conversely it stays disabled for an unmapped project where no
    // widget renders at all (desktop without a host `onAddSheetsForNavigation`
    // handler; mobile without a current drawing). The per-platform rules live in
    // `selectIsMiniMapWidgetShown`, shared with `MiniMapWeb` and
    // `useViewportSafeArea`.
    const miniMapAvailable = useAppSelector((state) => selectIsMiniMapWidgetShown(state, isProcoreMobileDesign));
    const sceneId = useSceneId();
    // The name useHandleOffline lists the offline record under — see `useOfflineSceneKey`.
    const offlineSceneKey = useOfflineSceneDirName();
    const {
        state: { view },
    } = useExplorerGlobals();
    const getViewpoint = useGetViewpoint();
    const guidanceSnapshot = useGuidanceSnapshot();
    const [getModelRevisionsQuery] = useLazyGetModelRevisionsQuery();
    const [modalOpen, setModalOpen] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [activeTab, setActiveTab] = useState<SettingsTab>("general");

    const handleTabChange = (_: React.SyntheticEvent, v: SettingsTab) => {
        setActiveTab(v);
    };

    const pickerOverlay = usePanelPickerOverlay();
    const isSectionVisible = useIsSettingsSectionVisible();
    const [openPickerKind, setOpenPickerKind] = useState<PickerKind | null>(null);

    const currentTier = deviceProfile.tier;

    // Option lists (stable across renders for use in picker callbacks)
    const unitSystemOptions = useMemo<PickerOption<UnitSystem>[]>(
        () => [
            { value: UnitSystem.imperial, label: t("imperial") },
            { value: UnitSystem.metric, label: t("metricMeters") },
            { value: UnitSystem.metricMM, label: t("metricMillimeters") },
        ],
        [t],
    );

    const performanceOptions = useMemo<PickerOption<string>[]>(
        () =>
            DEVICE_PROFILES.map((profile) => {
                const presetKey = getTierKey(profile.tier);
                return {
                    value: String(profile.tier),
                    label: t(`performancePresets.${presetKey}.name`),
                    description: t(`performancePresets.${presetKey}.description`),
                };
            }),
        [t],
    );

    const selectionResolutionOptions = useMemo<PickerOption<SelectionResolution>[]>(
        () =>
            SELECTION_RESOLUTION_OPTIONS.map((option) => ({
                value: option.value,
                label: t(`selectionResolutionOptions.${option.key}.name`),
                description: t(`selectionResolutionOptions.${option.key}.description`),
            })),
        [t],
    );

    const environmentOptions = useMemo<PickerOption<EnvironmentPresetId>[]>(
        () =>
            ENVIRONMENT_PRESETS.map((preset) => ({
                value: preset.id,
                label: t(preset.labelKey),
            })),
        [t],
    );

    const currentEnvironmentPresetId = useMemo(
        () => getEnvironmentPresetId(background, assetsUrl),
        [background, assetsUrl],
    );

    const getEnvironmentLabel = (id: EnvironmentPresetId | undefined) => {
        const option = environmentOptions.find((opt) => opt.value === id);
        if (option) {
            return option.label;
        }
        // Configured outside of the standard presets (e.g. a custom environment picked by
        // an Admin via Advanced Settings) -- fall back to showing the raw asset name.
        return background.url ? (background.url.split("/").filter(Boolean).at(-1) ?? background.url) : t("loading");
    };

    // Handlers
    const handleUnitSystemChange = (value: string) => {
        const newSystem = value as UnitSystem;
        if (newSystem !== unitSystem) {
            dispatch(explorerActions.setUnitSystem(newSystem));
            saveToStorage(StorageKey.UnitSystem, newSystem);
        }
    };

    const handlePerformanceChange = (value: string) => {
        const tier = Number(value) as GPUTier;
        if (tier !== currentTier) {
            const profile = getDeviceProfile(tier);
            dispatch(renderActions.setDeviceProfile(profile));
            try {
                localStorage["debugDeviceProfile"] = JSON.stringify(profile);
            } catch {
                // nada
            }
        }
    };

    const handleSelectionResolutionChange = (value: string) => {
        const newResolution = value as SelectionResolution;
        if (newResolution !== selectionResolution) {
            // A First/Last switch would otherwise leave a Last-object leaf selected
            // under First-object mode (or the reverse), so later clicks skip resolve.
            discardPendingContextMenuPaint();
            abortSharedSelection();
            dispatchHighlighted(highlightActions.setIds([]));
            dispatch(renderActions.setMainObject(undefined));
            dispatch(renderActions.clearMainObjects());
            dispatch(renderActions.setStamp(null));
            dispatch(explorerActions.setSelectionResolution(newResolution));
            saveToStorage(StorageKey.SelectionResolution, newResolution);
        }
    };

    const handleEnvironmentChange = (id: EnvironmentPresetId) => {
        if (id && id !== currentEnvironmentPresetId) {
            dispatch(renderActions.setBackground(getEnvironmentPresetBackground(id, assetsUrl)));
            saveToStorage(StorageKey.Environment, id);
        }
    };

    const handleMiniMapToggle = () => dispatch(persistFeatureToggle(FeatureToggle.MiniMapVisible, !isMiniMapVisible));
    const handleNavSphereToggle = () =>
        dispatch(persistFeatureToggle(FeatureToggle.AxisSphereVisible, !isAxisSphereVisible));
    const handleJoysticksToggle = () =>
        dispatch(persistFeatureToggle(FeatureToggle.JoysticksVisible, !isJoysticksVisible));
    const handleSpaceMouseToggle = () => {
        if (!isSpaceMouseEnabled) {
            // User action only — localStorage restore never goes through this handler.
            requestDroneOnNextConnect();
            if (pinholeController !== "flight") {
                setController("flight");
            }
            dispatch(persistFeatureToggle(FeatureToggle.SpaceMouseEnabled, true));
            return;
        }
        clearDroneOnNextConnect();
        dispatch(persistFeatureToggle(FeatureToggle.SpaceMouseEnabled, false));
    };

    const handleOpenModal = () => setModalOpen(true);
    const handleCloseModal = () => setModalOpen(false);

    const handleConfirmHomeViewUpdate = async () => {
        if (isUpdating || view?.activeController.kind === "panorama" || !companyId || !projectId) {
            return;
        }

        dispatch(explorerActions.setSnackbarMessage(null));

        setIsUpdating(true);
        try {
            const revisionsArgs = buildModelRevisionsArgs(
                { companyId, projectId, sceneVersionId: procoreContext?.sceneVersionId },
                sceneId,
            );
            const [viewpointPayload, revisions] = await Promise.all([
                getViewpoint({ createSnapshot: true, snapshotWidth: 3840, snapshotHeight: 2160, snapshotType: "jpeg" }),
                // preferCacheValue: reuse the shared session cache when populated.
                revisionsArgs ? getModelRevisionsQuery(revisionsArgs, true).unwrap() : Promise.resolve([]),
            ]);

            const revision = selectSceneRevision(revisions, {
                sceneVersionId: procoreContext?.sceneVersionId,
                sceneId,
            });

            if (!revision?.bim_model_id) {
                throw new Error("Could not resolve model id from revision");
            }

            const platformSceneId = revision.current_platform_scene_id;
            if (!platformSceneId) {
                throw new Error("Could not resolve platform scene id from revision");
            }

            if (!viewpointPayload.snapshot) {
                throw new Error("Failed to create snapshot for home viewpoint");
            }

            const existingHomeView = await getModelHomeView({
                companyId,
                projectId,
                modelId: revision.bim_model_id,
            });

            await saveModelHomeViewpoint({
                companyId,
                projectId,
                modelId: revision.bim_model_id,
                sceneId: platformSceneId,
                viewpoint: viewpointPayload,
                existingHomeView,
            });

            dispatch(renderActions.setInitialCamera(viewpointCameraToCadCamera(viewpointPayload.camera)));
            handleCloseModal();
            dispatch(explorerActions.setSnackbarMessage({ msg: t("homeViewUpdateSuccess"), closeAfter: 5000 }));
        } catch (e) {
            console.error("[Home View] Failed to update home view:", e);
            telemetry.trackCaughtException({
                source: "settingsContent.updateHomeView",
                category: "data",
                error: e,
                severity: "error",
            });
            dispatch(explorerActions.setSnackbarMessage({ msg: t("homeViewUpdateError"), closeAfter: 5000 }));
        } finally {
            setIsUpdating(false);
        }
    };

    const canUpdateHomeView = Boolean(view && companyId && projectId) && isOnline;
    const homeViewDisabledReason = !isOnline ? t("notAvailableOffline") : "";

    const updateHomeViewButton = (
        <Button
            variant="secondary"
            icon={<Home />}
            disabled={!canUpdateHomeView}
            onClick={handleOpenModal}
            data-testid="settings-update-home-view-button"
        >
            {t("updateHomeView")}
        </Button>
    );

    const homeViewSection = (
        <>
            <Box display="flex" flexDirection="column" gap="0.5rem">
                <FormLabel sx={{ fontWeight: 600, color: "text.primary" }}>{t("homeView")}</FormLabel>
                <Typography variant="body1" color="text.secondary">
                    {t("homeViewDescription")}
                </Typography>
                <Box>
                    {homeViewDisabledReason ? (
                        // Wrap in span so the tooltip can attach to a disabled element
                        <Tooltip title={homeViewDisabledReason} placement="top">
                            <span>{updateHomeViewButton}</span>
                        </Tooltip>
                    ) : (
                        updateHomeViewButton
                    )}
                </Box>
            </Box>

            <ConfirmModal
                open={modalOpen}
                headline={t("updateProjectHomeViewTitle")}
                onClose={handleCloseModal}
                width="sm"
            >
                <Modal.Body>
                    <Typography>{t("updateProjectHomeViewBody")}</Typography>
                </Modal.Body>
                <Modal.Footer>
                    <Modal.FooterButtons onClose={handleCloseModal}>
                        <Button variant="tertiary" onClick={handleCloseModal} disabled={isUpdating}>
                            {t("cancel")}
                        </Button>
                        <Button variant="primary" onClick={handleConfirmHomeViewUpdate} disabled={isUpdating}>
                            {isUpdating ? <Spinner size="sm" loading variant="button" /> : t("update")}
                        </Button>
                    </Modal.FooterButtons>
                </Modal.Footer>
            </ConfirmModal>
        </>
    );

    // Overlay picker helpers
    const closePickerOverlay = () => {
        setOpenPickerKind(null);
        pickerOverlay?.setOverlay(null);
    };

    const openPicker = (kind: PickerKind) => {
        if (!pickerOverlay) {
            return;
        }

        setOpenPickerKind(kind);

        const wrapDone = (handler: (v: string) => void) => (v: string) => {
            handler(v);
            closePickerOverlay();
        };

        switch (kind) {
            case "unitSystem":
                pickerOverlay.setOverlay(
                    <SettingsPickerView
                        title={t("measurementSystem")}
                        options={unitSystemOptions}
                        initialValue={unitSystem}
                        onCancel={closePickerOverlay}
                        onDone={wrapDone(handleUnitSystemChange)}
                        testIdPrefix="settings-measurement-system"
                    />,
                );
                break;
            case "performance":
                pickerOverlay.setOverlay(
                    <SettingsPickerView
                        title={t("performance")}
                        options={performanceOptions}
                        initialValue={String(currentTier)}
                        onCancel={closePickerOverlay}
                        onDone={wrapDone(handlePerformanceChange)}
                        testIdPrefix="settings-performance"
                    />,
                );
                break;
            case "selectionResolution":
                pickerOverlay.setOverlay(
                    <SettingsPickerView
                        title={t("selectionResolution")}
                        options={selectionResolutionOptions}
                        initialValue={selectionResolution}
                        onCancel={closePickerOverlay}
                        onDone={wrapDone(handleSelectionResolutionChange)}
                        testIdPrefix="settings-selection-resolution"
                    />,
                );
                break;
        }
    };

    // Clear overlay when this component unmounts (e.g. different drawer opened)
    useEffect(() => {
        return () => {
            pickerOverlay?.setOverlay(null);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Each section renders when the host allows it *and* its own runtime conditions hold, so the
    // host flag can only ever veto — leaving every default untouched for hosts that never call
    // `setSettingsSectionsVisible`.
    const showMeasurementSystem = isSectionVisible(SETTINGS_SECTION_IDS.MEASUREMENT_SYSTEM);
    const showPerformance = isSectionVisible(SETTINGS_SECTION_IDS.PERFORMANCE);
    const forcePerformanceIssuesTroubleshoot =
        new URLSearchParams(window.location.search).get("forcePerformanceIssuesTroubleshoot") === "true";
    const showPerformanceIssuesTroubleshoot =
        !isProcoreMobileDesign && (guidanceSnapshot?.isWindows || forcePerformanceIssuesTroubleshoot);
    const showSelectionResolution = isSectionVisible(SETTINGS_SECTION_IDS.SELECTION_RESOLUTION);
    const showEnvironment = isSectionVisible(SETTINGS_SECTION_IDS.ENVIRONMENT) && !useSlideInPicker;
    const showHomeViewMobile =
        isSectionVisible(SETTINGS_SECTION_IDS.HOME_VIEW) && useSlideInPicker && isAdminScene && hasModelRevision;
    const showHomeViewDesktop =
        isSectionVisible(SETTINGS_SECTION_IDS.HOME_VIEW) &&
        !useSlideInPicker &&
        isUpdateHomeViewEnabled &&
        hasModelRevision;
    const showInViewerToggles = isSectionVisible(SETTINGS_SECTION_IDS.SHOW_IN_VIEWER);
    const showNavigation = isSectionVisible(SETTINGS_SECTION_IDS.NAVIGATION) && isSpaceMouseDesktop;
    const showModelDownload =
        isSectionVisible(SETTINGS_SECTION_IDS.MODEL_DOWNLOAD) &&
        isProcoreMobileDesign &&
        Boolean(procoreContext?.enableFullSync) &&
        Boolean(procoreContext?.sceneSliceId) && // enabled only for slices
        hasModelRevision;

    const showHomeView = showHomeViewMobile || showHomeViewDesktop;

    const hasVisibleSection =
        showMeasurementSystem ||
        showPerformance ||
        showSelectionResolution ||
        showEnvironment ||
        showHomeView ||
        showInViewerToggles ||
        showNavigation ||
        showModelDownload;

    const openPickerSectionHidden =
        (openPickerKind === "unitSystem" && !showMeasurementSystem) ||
        (openPickerKind === "performance" && !showPerformance) ||
        (openPickerKind === "selectionResolution" && !showSelectionResolution);

    // A host hiding a section mid-interaction would otherwise strand its picker on top of the
    // section that is gone: the panel stays open, so the unmount cleanup above never runs. Done
    // would then write the very preference the host just hid.
    useEffect(() => {
        if (openPickerSectionHidden) {
            setOpenPickerKind(null);
            pickerOverlay?.setOverlay(null);
        }
    }, [openPickerSectionHidden, pickerOverlay]);

    // Same stale-interaction case: `modalOpen` lives here while `ConfirmModal` lives in
    // `homeViewSection`, so a host hiding `homeView` with the confirmation up would pop it back
    // open unprompted the next time the section is shown.
    useEffect(() => {
        if (!showHomeView) {
            setModalOpen(false);
        }
    }, [showHomeView]);

    return (
        <MuiBox display="flex" flexDirection="column" height="100%" bgcolor="#fff">
            {/* Tabs header — desktop only; mobile renders General content directly */}
            {!useSlideInPicker && (
                <MuiBox
                    flexShrink={0}
                    bgcolor="#fff"
                    borderBottom={activeTab === "general" ? "1px solid var(--color-border-separator, #d6dadc)" : "none"}
                >
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        sx={{
                            minHeight: 30,
                            px: "16px",
                            bgcolor: "#fff",
                            "& .MuiTabs-indicator": {
                                backgroundColor: "var(--color-border-navigation-select, #232729)",
                                height: "3px",
                            },
                            "& .MuiTab-root": {
                                textTransform: "none",
                                fontSize: "0.875rem",
                                fontWeight: 400,
                                minHeight: 30,
                                py: "4px",
                                px: "4px",
                                mr: "24px",
                                color: "var(--color-text-secondary, #5e696e)",
                            },
                            "& .Mui-selected": {
                                color: "var(--color-text-primary, #232729) !important",
                                fontWeight: 600,
                            },
                        }}
                    >
                        <Tab value="general" label={t("settingsShortcuts.generalTab")} disableRipple />
                        <Tab value="shortcuts" label={t("settingsShortcuts.shortcutsTab")} disableRipple />
                    </Tabs>
                </MuiBox>
            )}
            {!useSlideInPicker && activeTab === "shortcuts" && <ShortcutsTab />}
            {(useSlideInPicker || activeTab === "general") && (
                <MuiBox flex={1} overflow="auto" display="flex" flexDirection="column" gap="1rem" padding="1rem">
                    {!hasVisibleSection && (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            textAlign="center"
                            data-testid="settings-empty-state"
                        >
                            {t("noSettingsAvailable")}
                        </Typography>
                    )}

                    {showHeadlightSettings && <HeadlightSettings />}

                    {/* Measurement System */}
                    {showMeasurementSystem &&
                        (useSlideInPicker ? (
                            <PickerRow
                                label={t("measurementSystem")}
                                valueLabel={
                                    unitSystemOptions.find((opt) => opt.value === unitSystem)?.label ?? unitSystem
                                }
                                onClick={() => openPicker("unitSystem")}
                                testId="settings-measurement-system-picker-row"
                            />
                        ) : (
                            <FormControl fullWidth size="small">
                                <FormLabel sx={sectionLabelSx}>{t("measurementSystem")}</FormLabel>
                                <Select
                                    value={unitSystem}
                                    onChange={(e) => handleUnitSystemChange(e.target.value)}
                                    size="small"
                                    fullWidth
                                    data-testid="settings-measurement-system-select"
                                >
                                    {unitSystemOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        ))}

                    {/* Performance */}
                    {showPerformance &&
                        (useSlideInPicker ? (
                            <PickerRow
                                label={t("performance")}
                                valueLabel={t(`performancePresets.${getTierKey(currentTier)}.name`)}
                                onClick={() => openPicker("performance")}
                                testId="settings-performance-picker-row"
                            />
                        ) : (
                            <Box display="flex" flexDirection="column" gap="0.5rem">
                                <FormControl fullWidth size="small">
                                    <FormLabel sx={sectionLabelSx}>{t("performance")}</FormLabel>
                                    <Select
                                        value={String(currentTier)}
                                        onChange={(e) => handlePerformanceChange(String(e.target.value))}
                                        size="small"
                                        fullWidth
                                        renderValue={(value) =>
                                            performanceOptions.find((opt) => opt.value === value)?.label ?? ""
                                        }
                                        data-testid="settings-performance-select"
                                    >
                                        {performanceOptions.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                <Box>
                                                    <Typography variant="body2">{option.label}</Typography>
                                                    <Typography variant="body1" color="text.secondary">
                                                        {option.description}
                                                    </Typography>
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                {showPerformanceIssuesTroubleshoot ? (
                                    <Box
                                        display="flex"
                                        flexDirection="row"
                                        gap="0.25rem"
                                        style={{
                                            fontFamily: "Inter",
                                            fontWeight: 400,
                                            fontSize: "12px",
                                            lineHeight: "16px",
                                            letterSpacing: "0.25px",
                                        }}
                                    >
                                        <Typography variant="inherit" color="text.secondary">
                                            {t("performanceIssuesQuestion")}
                                        </Typography>
                                        <Typography
                                            variant="inherit"
                                            color="rgba(29, 92, 201, 1)"
                                            sx={{ cursor: "pointer" }}
                                            onClick={() => openGuidanceWizard(WizardTrigger.Troubleshoot)}
                                        >
                                            {t("performanceIssuesLink")}
                                        </Typography>
                                    </Box>
                                ) : null}
                            </Box>
                        ))}

                    {/* Selection Resolution */}
                    {showSelectionResolution &&
                        (useSlideInPicker ? (
                            <PickerRow
                                label={t("selectionResolution")}
                                valueLabel={
                                    selectionResolutionOptions.find((opt) => opt.value === selectionResolution)
                                        ?.label ?? ""
                                }
                                onClick={() => openPicker("selectionResolution")}
                                testId="settings-selection-resolution-picker-row"
                            />
                        ) : (
                            <FormControl fullWidth size="small">
                                <FormLabel sx={sectionLabelSx}>{t("selectionResolution")}</FormLabel>
                                <Select
                                    value={selectionResolution}
                                    onChange={(e) => handleSelectionResolutionChange(e.target.value)}
                                    size="small"
                                    fullWidth
                                    renderValue={(value) =>
                                        selectionResolutionOptions.find((opt) => opt.value === value)?.label ?? ""
                                    }
                                    data-testid="settings-selection-resolution-select"
                                >
                                    {selectionResolutionOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            <Box>
                                                <Typography variant="body2">{option.label}</Typography>
                                                <Typography
                                                    variant="body1"
                                                    color="text.secondary"
                                                    sx={{ whiteSpace: "normal" }}
                                                >
                                                    {option.description}
                                                </Typography>
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        ))}

                    {/* Environment — desktop only */}
                    {showEnvironment && (
                        <FormControl fullWidth size="small">
                            <FormLabel sx={sectionLabelSx}>{t("environment")}</FormLabel>
                            <Select
                                value={currentEnvironmentPresetId ?? ""}
                                onChange={(e) => handleEnvironmentChange(e.target.value as EnvironmentPresetId)}
                                size="small"
                                fullWidth
                                displayEmpty
                                renderValue={() => getEnvironmentLabel(currentEnvironmentPresetId)}
                                data-testid="settings-environment-select"
                            >
                                {environmentOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    {/* Home View — mobile only, admin-gated, Model Manager only */}
                    {showHomeViewMobile && homeViewSection}

                    {/* Show in Viewer toggles */}
                    {showInViewerToggles && (
                        <MuiBox
                            display="flex"
                            flexDirection="column"
                            gap="0.5rem"
                            sx={{
                                "& label[class*='StyledSwitchContainer']": {
                                    border: "1px solid transparent !important",
                                    boxShadow: "none !important",
                                    outline: "none !important",
                                },
                            }}
                        >
                            <FormLabel sx={{ fontWeight: 600, color: "text.primary" }}>{t("showInViewer")}</FormLabel>

                            {/* 2D Mini Map — always shown; disabled until a drawing is loaded */}
                            <ToggleWithText
                                checked={isMiniMapVisible && miniMapAvailable}
                                onChange={handleMiniMapToggle}
                                label="miniMap"
                                disabled={!miniMapAvailable}
                            />

                            {/* Navigation Sphere — always shown on all form factors */}
                            <ToggleWithText
                                checked={isAxisSphereVisible}
                                onChange={handleNavSphereToggle}
                                label="navigationSphere"
                            />

                            {/* Joysticks — mobile only */}
                            {isProcoreMobileDesign && (
                                <ToggleWithText
                                    checked={isJoysticksVisible}
                                    onChange={handleJoysticksToggle}
                                    label="joysticks"
                                />
                            )}
                        </MuiBox>
                    )}

                    {/* SpaceMouse — behind the host's rollout flag, desktop only; enabling prompts the browser
                for local-network access so the viewer can reach the local 3Dconnexion driver. When that
                permission is blocked Chrome won't re-prompt, so we disable the toggle and explain how
                to re-grant it. Hidden on Procore iOS/Android and phone-sized viewports — the driver
                handshake is a desktop loopback session. */}
                    {showNavigation && (
                        <MuiBox
                            display="flex"
                            flexDirection="column"
                            gap="0.5rem"
                            sx={{
                                "& label[class*='StyledSwitchContainer']": {
                                    border: "1px solid transparent !important",
                                    boxShadow: "none !important",
                                    outline: "none !important",
                                },
                            }}
                        >
                            <FormLabel sx={{ fontWeight: 600, color: "text.primary" }}>
                                {t("externalControllers")}
                            </FormLabel>
                            <ToggleWithText
                                checked={isSpaceMouseEnabled}
                                onChange={handleSpaceMouseToggle}
                                label="spaceMouse.title"
                                disabled={spaceMousePermission === "denied"}
                            />
                            {/* One note at a time — see spaceMouseNoteKey for which wins and why. No retry button
                        on the driver note: what it asks for (install 3DxWare, restart the machine) ends
                        the page's session anyway, and toggling this setting re-attempts. */}
                            {spaceMouseNote && spaceMouseNoteLevel && (
                                <Alert
                                    severity={spaceMouseNoteLevel}
                                    icon={false}
                                    data-testid="settings-space-mouse-note"
                                    sx={{
                                        border: "1px solid",
                                        borderRadius: "3px",
                                        padding: "4px 8px",
                                        fontWeight: 400,
                                        fontSize: "0.875rem",
                                        lineHeight: 1.43,
                                        "& .MuiAlert-message": { p: 0 },
                                        // After the border shorthand, which otherwise resets border-color.
                                        ...SPACE_MOUSE_NOTE_COLORS[spaceMouseNoteLevel],
                                    }}
                                >
                                    {t(spaceMouseNote)}
                                </Alert>
                            )}
                        </MuiBox>
                    )}

                    {/* Home View — desktop only, feature-toggle-gated, Model Manager only */}
                    {showHomeViewDesktop && homeViewSection}

                    {/* Model Download: mobile-only, host opt-in via enableFullSync, Model Manager only */}
                    {showModelDownload && <ModelDownloadSection sceneKey={offlineSceneKey} />}
                </MuiBox>
            )}
        </MuiBox>
    );
}
